import axios from 'axios';

class DropboxService {
  constructor() {
    this.accessToken = import.meta.env.VITE_DROPBOX_ACCESS_TOKEN;
    this.apiUrl = 'https://api.dropboxapi.com/2';
    this.contentUrl = 'https://content.dropboxapi.com/2';
    
    console.log('🔧 [Dropbox] Initializing service...');
    console.log('🔑 [Dropbox] Access token exists:', !!this.accessToken);
    console.log('🌐 [Dropbox] API URL:', this.apiUrl);
    console.log('📁 [Dropbox] Content URL:', this.contentUrl);
  }

  async uploadFile(file, path) {
    console.log('📁 [Dropbox] ===== UPLOAD FILE START =====');
    console.log('📁 [Dropbox] File:', file.name);
    console.log('📁 [Dropbox] Size:', file.size);
    console.log('📁 [Dropbox] Type:', file.type);
    console.log('📁 [Dropbox] Path:', path);
    
    try {
      const url = `${this.contentUrl}/files/upload`;
      console.log('🌐 [Dropbox] Upload URL:', url);
      
      const dropboxApiArg = JSON.stringify({
        path: `/${path}`,
        mode: 'add',
        autorename: true
      });
      console.log('📤 [Dropbox] Dropbox-API-Arg:', dropboxApiArg);
      
      const response = await axios.post(url, file, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/octet-stream',
          'Dropbox-API-Arg': dropboxApiArg
        }
      });
      
      console.log('📥 [Dropbox] Upload response:', response.data);
      console.log('✅ [Dropbox] ===== UPLOAD FILE SUCCESS =====');
      
      return response.data;
    } catch (error) {
      console.error('❌ [Dropbox] ===== UPLOAD FILE FAILED =====');
      console.error('💥 [Dropbox] Error:', error);
      console.error('📁 [Dropbox] File:', file.name);
      console.error('📁 [Dropbox] Path:', path);
      
      if (error.response) {
        console.error('🌐 [Dropbox] Response status:', error.response.status);
        console.error('🌐 [Dropbox] Response data:', error.response.data);
        console.error('🌐 [Dropbox] Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('🌐 [Dropbox] No response received:', error.request);
      } else {
        console.error('🌐 [Dropbox] Request setup error:', error.message);
      }
      
      console.error('🔗 [Dropbox] Error stack:', error.stack);
      
      throw new Error(`Dropbox upload error: ${error.message}`);
    }
  }

  async uploadProfilePicture(file, contactId) {
    console.log('📸 [Dropbox] ===== UPLOAD PROFILE PICTURE START =====');
    console.log('📸 [Dropbox] Contact ID:', contactId);
    console.log('📸 [Dropbox] File:', file.name);
    
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `contact_${contactId}_${timestamp}.${fileExtension}`;
      const path = `crm/profile-pictures/${fileName}`;
      
      console.log('📸 [Dropbox] Generated filename:', fileName);
      console.log('📸 [Dropbox] Full path:', path);
      
      // Upload original image
      const uploadResponse = await this.uploadFile(file, path);
      console.log('📸 [Dropbox] File uploaded:', uploadResponse);
      
      // Create shared link for the uploaded file
      console.log('🔗 [Dropbox] Creating shared link...');
      const sharedLinkResponse = await this.createSharedLink(uploadResponse.path_lower);
      console.log('🔗 [Dropbox] Shared link created:', sharedLinkResponse);
      
      // Generate thumbnail URL (Dropbox automatically provides thumbnails)
      const thumbnailUrl = sharedLinkResponse.url.replace('dl=0', 'dl=1&size=thumbnail');
      console.log('🖼️ [Dropbox] Thumbnail URL:', thumbnailUrl);
      
      const result = {
        id: uploadResponse.id,
        path: uploadResponse.path_lower,
        url: sharedLinkResponse.url,
        thumbnailUrl: thumbnailUrl,
        fileName: fileName
      };
      
      console.log('✅ [Dropbox] ===== UPLOAD PROFILE PICTURE SUCCESS =====');
      console.log('📸 [Dropbox] Result:', result);
      
      return result;
    } catch (error) {
      console.error('❌ [Dropbox] ===== UPLOAD PROFILE PICTURE FAILED =====');
      console.error('💥 [Dropbox] Error:', error);
      console.error('📸 [Dropbox] Contact ID:', contactId);
      console.error('📸 [Dropbox] File:', file.name);
      console.error('🔗 [Dropbox] Error stack:', error.stack);
      
      throw new Error(`Profile picture upload error: ${error.message}`);
    }
  }

  async createSharedLink(path) {
    console.log('🔗 [Dropbox] ===== CREATE SHARED LINK START =====');
    console.log('🔗 [Dropbox] Path:', path);
    
    try {
      const url = `${this.apiUrl}/sharing/create_shared_link_with_settings`;
      console.log('🌐 [Dropbox] Request URL:', url);
      
      const requestData = {
        path: path,
        settings: {
          requested_visibility: 'public'
        }
      };
      console.log('📤 [Dropbox] Request data:', requestData);
      
      const response = await axios.post(url, requestData, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📥 [Dropbox] Shared link response:', response.data);
      console.log('✅ [Dropbox] ===== CREATE SHARED LINK SUCCESS =====');
      
      return response.data;
    } catch (error) {
      console.error('❌ [Dropbox] ===== CREATE SHARED LINK FAILED =====');
      console.error('💥 [Dropbox] Error:', error);
      console.error('🔗 [Dropbox] Path:', path);
      
      if (error.response) {
        console.error('🌐 [Dropbox] Response status:', error.response.status);
        console.error('🌐 [Dropbox] Response data:', error.response.data);
      }
      
      console.error('🔗 [Dropbox] Error stack:', error.stack);
      
      throw new Error(`Dropbox shared link error: ${error.message}`);
    }
  }

  async createThumbnailUrl(fileUrl) {
    console.log('🖼️ [Dropbox] Creating thumbnail URL for:', fileUrl);
    try {
      // Convert to thumbnail URL with proper parameters
      const thumbnailUrl = fileUrl.replace('dl=0', 'dl=1&size=thumbnail&bounding_box=64x64');
      console.log('🖼️ [Dropbox] Thumbnail URL:', thumbnailUrl);
      return thumbnailUrl;
    } catch (error) {
      console.error('❌ [Dropbox] Thumbnail generation error:', error);
      throw new Error(`Thumbnail generation error: ${error.message}`);
    }
  }

  async deleteFile(path) {
    console.log('🗑️ [Dropbox] ===== DELETE FILE START =====');
    console.log('🗑️ [Dropbox] Path:', path);
    
    try {
      const url = `${this.apiUrl}/files/delete_v2`;
      console.log('🌐 [Dropbox] Request URL:', url);
      
      const requestData = { path: path };
      console.log('📤 [Dropbox] Request data:', requestData);
      
      const response = await axios.post(url, requestData, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📥 [Dropbox] Delete response:', response.data);
      console.log('✅ [Dropbox] ===== DELETE FILE SUCCESS =====');
      
      return response.data;
    } catch (error) {
      console.error('❌ [Dropbox] ===== DELETE FILE FAILED =====');
      console.error('💥 [Dropbox] Error:', error);
      console.error('🗑️ [Dropbox] Path:', path);
      
      if (error.response) {
        console.error('🌐 [Dropbox] Response status:', error.response.status);
        console.error('🌐 [Dropbox] Response data:', error.response.data);
      }
      
      console.error('🔗 [Dropbox] Error stack:', error.stack);
      
      throw new Error(`Dropbox delete error: ${error.message}`);
    }
  }

  async listFiles(path = '') {
    console.log('📂 [Dropbox] Listing files in path:', path);
    try {
      const response = await axios.post(`${this.apiUrl}/files/list_folder`, {
        path: path
      }, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('📂 [Dropbox] Files listed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [Dropbox] List files error:', error);
      throw new Error(`Dropbox list files error: ${error.message}`);
    }
  }
}

export const dropboxService = new DropboxService();